import React from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Crown, Trophy } from 'lucide-react';

interface ProtectedProRouteProps {
  children: React.ReactNode;
}

/**
 * Protège les routes réservées aux utilisateurs avec un abonnement Pro
 * - Les admins ont un accès complet
 * - Les éleveurs ont un accès complet
 * - Les utilisateurs Pro ont accès
 * - Les utilisateurs Premium et Gratuit sont bloqués
 */
const ProtectedProRoute: React.FC<ProtectedProRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { subscriptionPlan, isAdmin, isBreeder, loading: subLoading } = useSubscription();
  const location = useLocation();

  const loading = authLoading || subLoading;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Vérification de l'abonnement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Admins et éleveurs ont accès complet, sinon vérifier plan Pro
  const hasProAccess = isAdmin || isBreeder || subscriptionPlan === 'pro';

  if (!hasProAccess) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <Card className="max-w-xl w-full border-2 border-yellow-500/30">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 rounded-full bg-yellow-500/10 w-fit">
              <Crown className="h-8 w-8 text-yellow-500" />
            </div>
            <CardTitle className="flex items-center justify-center gap-2 text-xl">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Fonctionnalité Pro Exclusive
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Cette fonctionnalité est réservée aux abonnés du <strong>plan Pro</strong>.
              Débloquez les compétitions, statistiques avancées et bien plus encore !
            </p>
            
            <div className="bg-muted/50 rounded-lg p-4 text-left">
              <p className="font-medium mb-2">Le plan Pro inclut :</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>✓ Suivi des compétitions et classements</li>
                <li>✓ Statistiques avancées</li>
                <li>✓ Profil éleveur professionnel</li>
                <li>✓ IA vétérinaire illimitée</li>
                <li>✓ 10 Go de stockage</li>
              </ul>
            </div>
            
            <div className="flex flex-wrap gap-3 justify-center pt-2">
              <Button asChild className="bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700">
                <Link to="/subscription" state={{ from: location }}>
                  Passer au plan Pro
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/">
                  Retour à l'accueil
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedProRoute;
